import { FormattedMessage } from "react-intl";
import simpleFetch from "../../../../utils/simpleFetch";

import {
    LATEST_MEMBER_SYNCS,
    LATEST_DIVISION_SYNCS,
    LATEST_DEPENDENT_PERSON_SYNCS,
    CLIENT_SYNCS,
    LATEST_CLASS_SYNCS,
    IS_MEMBER_COVERAGE_SYNCHRONIZED,
    IS_DEPENDENT_COVERAGE_SYNCHRONIZED,
    LATEST_MEMBER_SYNCS_V2,
} from "../../../utils/data/models";
import { toast } from "react-toastify";
import React from "react/lib/React";
import { FAILURE, STARTED, SUCCESS } from "../../dashboard/synchronization/enums/syncStatusTypes";
import OverlayWithToolTip from "../Overlay/OverlayWithToolTip";
import { toCamelCase } from "../form/DBEnumStringHelper";
import userHasAccess from "../../../utils/userHasAccess";
import { INTERNAL_ROLES, HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER, AGENT, PLAN_ADMINISTRATOR } from "../../../utils/Roles";
import noSyncLogo from "../../../assets/images/noSyncLogo.png";
import { ACTIVE, IMPLEMENTATION, INACTIVE } from "../../dashboard/clients/enums/clientStatusTypes";
import { ELIGIBILITY, NO_SYNC, TED_DESJARDINS } from "../../dashboard/clients/enums/synchronizationOptions";
import { MANUAL, RECURRING } from "../../dashboard/clients/enums/billingPeriodTypes";
import {
    MEMBER,
    DEPENDENT_PERSON,
    CLIENT,
    CLASS,
    DIVISION,
} from "../../dashboard/synchronization/enums/syncElementTypes";

export const getLatestMemberSyncsV2 = async (clientId, memberIds, onlyProfiles = false, coveragesExclusively = false) => {
    if (!userHasAccess([...HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER, AGENT, PLAN_ADMINISTRATOR], true)) {
        return;
    }
    try {
        const query = { member_ids: memberIds.length ? memberIds : "", only_profiles: onlyProfiles, coverages_exclusively : coveragesExclusively};
        const response = await simpleFetch("get", LATEST_MEMBER_SYNCS_V2.path.replace("{}", clientId), { query });
        return response;
    } catch (error) {
        handleFetchError(error);
    }
};

export const getLatestMemberSyncs = (clientId, memberIds, dataActions, cb) => {
    if (!userHasAccess([...HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER, AGENT, PLAN_ADMINISTRATOR], true)) {
        return;
    }
    const memberIdsParam = memberIds && memberIds.length > 0 ? memberIds : "";
    let model = JSON.parse(JSON.stringify(LATEST_MEMBER_SYNCS));
    model.path = model.path.replace("{}", clientId);
    const result = dataActions
        .getDataItem(model, "", {
            memberIds: memberIdsParam,
        })
        .then((response) => {
            if (cb) {
                cb(response);
            }
        })
        .catch(() => {
            toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                type: toast.TYPE.ERROR,
                autoClose: false,
            });
        });
    return result;
};

export const isMemberCoverageSynchronized = (memberCoverageId, dataActions, cb) => {
    if (!memberCoverageId) return false;
    let model = JSON.parse(JSON.stringify(IS_MEMBER_COVERAGE_SYNCHRONIZED));
    model.path = model.path.replace("{}", memberCoverageId);
    const result = dataActions
        .getDataItem(model, "", {}, {}, true)
        .then((response) => {
            if (cb) {
                cb(response);
            }
        })
        .catch(() => {
            toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                type: toast.TYPE.ERROR,
                autoClose: false,
            });
        });
    return result;
};

export const isDependentCoverageSynchronized = (dependentPersonCoverageId, dataActions, cb) => {
    if (!dependentPersonCoverageId) return false;
    let model = JSON.parse(JSON.stringify(IS_DEPENDENT_COVERAGE_SYNCHRONIZED));
    model.path = model.path.replace("{}", dependentPersonCoverageId);
    const result = dataActions
        .getDataItem(model, "", {}, {}, true)
        .then((response) => {
            if (cb) {
                cb(response);
            }
        })
        .catch(() => {
            toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                type: toast.TYPE.ERROR,
                autoClose: false,
            });
        });
    return result;
};

export const isHashValid = (obj, syncObj, partnerHashKey, hashKey) => {
    if (obj[partnerHashKey]) {
        return obj[partnerHashKey] === syncObj[partnerHashKey];
    }
    return obj[hashKey] === syncObj[partnerHashKey];
};

export const isClientEligibleForSync = (client) => {
    /**
     * Note: If eligible condition changes, translation string must also be updated.
     */
    const isClientStatusValid = [IMPLEMENTATION.value, ACTIVE.value, INACTIVE.value].includes(client.clientStatusType);

    let isClientSynchronizable = client.syncType === ELIGIBILITY.value;
    if (client.clientBillingPeriodType == MANUAL.value) isClientSynchronizable = true;
    return isClientStatusValid && isClientSynchronizable && client.isArchived === false;
};

export const isMemberEligibleForSync = (member, client) => {
    /**
     * Note: If eligible condition changes, translation string must also be updated.
     */
    if (!member || !client) {
        return false;
    }
    const isClientStatusValidForMemberSync = [ACTIVE.value, INACTIVE.value].includes(client.clientStatusType);
    const isMemberNonTransmis =
        `${member.firstName.toLowerCase().trim()} ${member.lastName.toLowerCase().trim()}` === "non transmis";
    const hasAtLeastOneMemberCoverage = member.memberCoverages && member.memberCoverages.length > 0;
    let isMemberEligibleForSync =
        isClientStatusValidForMemberSync &&
        member.isArchived === false &&
        member.isSyncDisabled === false &&
        hasAtLeastOneMemberCoverage &&
        !isMemberNonTransmis;

    if (client.clientBillingPeriodType === RECURRING.value) {
        isMemberEligibleForSync = isMemberEligibleForSync && member.countryCode === "CA";
    }
    return isMemberEligibleForSync;
};

const dependentHasAtLeastOneDependentCoverage = (dependent) => {
    return dependent.dependentCoverages && dependent.dependentCoverages.length > 0;
};

const isElementNonArchived = (element) => {
    return element && !element.isArchived;
};

const isDependentEligibleForSync = (dependent) => {
    return isElementNonArchived(dependent) && dependentHasAtLeastOneDependentCoverage(dependent);
};

export const isArchivedEventError = (syncObj) => {
    return syncObj && syncObj.eventName && syncObj.eventName.includes("Archived") && syncObj.status !== SUCCESS;
};

export const getErrorValues = (item, customComponent = null, placement = "top") => {
    const clientId = item && item.client && item.client.clientId;
    if (clientId == undefined || !clientId) {
        return <FormattedMessage id="common.nd" />;
    }

    switch (item.elementType) {
        case DEPENDENT_PERSON.value:
            return getOverlayForSync(
                item,
                item,
                "dependents.datatable",
                "dependentPersonPartnerHash",
                "dependentPersonHash",
                "dependentPerson",
                true,
                false,
                customComponent,
                placement
            );
        case MEMBER.value:
            return getOverlayForSync(
                item,
                item,
                "members.datatable",
                "memberPartnerHash",
                "memberHash",
                "member",
                true,
                false,
                customComponent,
                placement
            );
        case CLASS.value:
            return getOverlayForSync(
                item,
                item,
                "clientClasses.datatable",
                "",
                "",
                "",
                false,
                false,
                customComponent,
                placement
            );
        case DIVISION.value:
            return getOverlayForSync(
                item,
                item,
                "divisions.datatable",
                "divisionPartnerHash",
                "divisionHash",
                "division",
                true,
                false,
                customComponent,
                placement
            );
        case CLIENT.value:
            return getOverlayForSync(
                item,
                item,
                "clients.datatable",
                "clientPartnerHash",
                "clientHash",
                "client",
                true,
                false,
                customComponent,
                placement
            );

        default:
            return <FormattedMessage id="common.nd" />;
    }
};

export const getOverlayForSync = (
    obj,
    syncObj,
    translationMessagePrefix,
    partnerHashKey,
    hashKey,
    mainObjKey,
    checkHashValidity = true,
    verifyEligibility = true,
    customComponent = null,
    placement = "top"
) => {
    /**
     * Function that will handle display of overlay status with tooltip.
     * For non-synchronizable elements, the obj param needs to have the necessary elements to be checked.
     * e.g. if we pass a division, we need to add a client object to the "obj" parameter, so the function can check
     * the necessary requirements.
     */
    let toolTipId = `${translationMessagePrefix}.toolTips.syncToolTipWarning`;
    let img = null;
    if (obj) {
        if (isArchivedEventError(syncObj)) {
            if (syncObj.status === FAILURE) {
                if (syncObj.errors) {
                    toolTipId = `${translationMessagePrefix}.toolTips.syncToolTipWarningWithError`;
                } else {
                    toolTipId = `${translationMessagePrefix}.toolTips.syncToolTipWarning`;
                }
            }
        } else if (Object.hasOwn(obj, "client") && verifyEligibility && !isClientEligibleForSync(obj.client)) {
            img = noSyncLogo;
            toolTipId = "clients.synchronizationStatus.nonSyncClient";
        } else if (
            Object.hasOwn(obj, "member") &&
            verifyEligibility &&
            !isMemberEligibleForSync(obj.member, obj.client)
        ) {
            img = noSyncLogo;
            toolTipId = "members.datatable.toolTips.memberNoSync";
        } else if (
            Object.hasOwn(obj, "dependentPerson") &&
            verifyEligibility &&
            !isDependentEligibleForSync(obj.dependentPerson)
        ) {
            img = noSyncLogo;
            toolTipId = `${translationMessagePrefix}.toolTips.dependentNoSync`;
        } else if (Object.hasOwn(obj, "division") && verifyEligibility && !isElementNonArchived(obj.division)) {
            img = noSyncLogo;
            toolTipId = `${translationMessagePrefix}.toolTips.syncToolTipArchived`;
        } else if (Object.hasOwn(obj, "clientClass") && verifyEligibility && !isElementNonArchived(obj.clientClass)) {
            img = noSyncLogo;
            toolTipId = `${translationMessagePrefix}.toolTips.syncToolTipArchived`;
        } else if (syncObj) {
            if (Object.hasOwn(syncObj, "relatedCoverageSyncWarning") && syncObj.relatedCoverageSyncWarning === true) {
                toolTipId = `${translationMessagePrefix}.toolTips.relatedCoverageToolTipWarning`;
            }
            else if (syncObj.status === STARTED) {
                toolTipId = `${translationMessagePrefix}.toolTips.syncToolTipStarted`;
            } else if (syncObj.status === FAILURE && syncObj.errors && syncObj.errors.length > 0) {
                toolTipId = `${translationMessagePrefix}.toolTips.syncToolTipWarningWithError`;
            } else if (syncObj.status === SUCCESS) {
                const isValidHashStatus = checkHashValidity
                    ? isHashValid(obj[mainObjKey], syncObj, partnerHashKey, hashKey)
                    : true;
                if (isValidHashStatus) toolTipId = `${translationMessagePrefix}.toolTips.syncToolTip`;
            }
        }
    }

    if (toolTipId.includes("Error")) {
        if (customComponent) {
            return (
                <OverlayWithToolTip
                    toolTipId={toolTipId}
                    errors={syncObj.errors}
                    customComponent={customComponent}
                    placement={placement}
                />
            );
        } else {
            return <OverlayWithToolTip toolTipId={toolTipId} errors={syncObj.errors} placement={placement} />;
        }
    }

    if (customComponent) {
        return (
            <OverlayWithToolTip
                toolTipId={toolTipId}
                img={img}
                customComponent={customComponent}
                placement={placement}
            />
        );
    } else {
        return <OverlayWithToolTip toolTipId={toolTipId} img={img} placement={placement} />;
    }
};

export const getErrorString = (errors, intl) => {
    const error = errors[errors.length - 1];
    if (error && error.hasOwnProperty("key")) {
        let key = toCamelCase(error["key"]);
        const identifier = error.hasOwnProperty("identifier") ? error["identifier"] : {};
        let displayedIdentifiers = {};
        if (Object.keys(identifier).length !== 0) {
            Object.keys(identifier).forEach((key) => {
                if (key == "field") {
                    if (identifier["field"] != "" && identifier["field"] != undefined) {
                        displayedIdentifiers[key] = intl.formatHTMLMessage({
                            id: `clients.client.processSync.errorIdentifiers.fields.${toCamelCase(identifier[key])}`,
                            defaultMessage: identifier[key],
                        });
                    } else {
                        key = `${key}Generic`;
                    }
                } else displayedIdentifiers[toCamelCase(key)] = toCamelCase(identifier[key]);
            });
        } else {
            const rawMessage = intl.formatMessage({ id: `clients.client.processSync.errorIdentifiers.${key}` });
            const variablePattern = /\{(\w+)\}/g;
            const variables = [...rawMessage.matchAll(variablePattern)].map(match => match[1]);
            const deepestVariable = variables.pop() || null;
            if (deepestVariable) {
                displayedIdentifiers[deepestVariable] = 'NONE';
            }
        }
        return intl.formatMessage(
            {
                id: `clients.client.processSync.errorIdentifiers.${key}`,
            },
            displayedIdentifiers
        );
    } else {
        return intl.formatMessage({
            id: `clients.client.processSync.errorIdentifiers.unexpectedError`,
        });
    }
};

const handleFetchError = (error) => {
    if (error.status) {
        if (error.status > 399 && error.status < 500) {
            toast(<FormattedMessage id="validation.customMessages.clientError" />, {
                type: toast.TYPE.ERROR,
                autoClose: false,
            });
        } else if (error.status >= 500) {
            toast(<FormattedMessage id="genericServerError" />, {
                type: toast.TYPE.ERROR,
                autoClose: false,
            });
        }
    } else {
        //Another kind of untreatable error like a bad url
        toast(<FormattedMessage id="validation.customMessages.serverErrors.generic" />, {
            type: toast.TYPE.ERROR,
            autoClose: false,
        });
    }
};

export const getLatestDependentSyncs = (clientId, dependentPersonIds, dataActions, cb, onlyProfiles = false) => {
    const dependentPersonIdsParam = dependentPersonIds && dependentPersonIds.length > 0 ? dependentPersonIds : "";
    let model = JSON.parse(JSON.stringify(LATEST_DEPENDENT_PERSON_SYNCS));
    model.path = model.path.replace("{}", clientId);
    const result = dataActions
        .getDataItem(model, "", {
            dependent_person_ids: dependentPersonIdsParam,
            only_profiles: onlyProfiles
        }, {}, false, model.path)
        .then((response) => {
            if (response) {
                if (cb) {
                    cb(response);
                }
            }
        })
        .catch(() => {
            toast(<FormattedMessage id="validation.customMessages.serverErrors.genericError" />, {
                type: toast.TYPE.ERROR,
                autoClose: false,
            });
        });
    return result;
};

export const getLatestDivisionSyncs = async (clientId, divisionIds = []) => {
    try {
        const query = { division_ids: divisionIds.length ? divisionIds : "" };
        const response = await simpleFetch("get", LATEST_DIVISION_SYNCS.path.replace("{}", clientId), { query });
        return response;
    } catch (error) {
        handleFetchError(error);
    }
};

export const getClientSyncs = async (clientId) => {
    try {
        const response = await simpleFetch("get", CLIENT_SYNCS.path.replace("{}", clientId), {});
        return response;
    } catch (error) {
        handleFetchError(error);
    }
};

export const getLatestClassSyncs = async (clientId, classIds = []) => {
    try {
        const query = { class_ids: classIds.length ? classIds : "" };
        const response = await simpleFetch("get", LATEST_CLASS_SYNCS.path.replace("{}", clientId), { query });
        return response;
    } catch (error) {
        handleFetchError(error);
    }
};

export const canViewSyncStatus = (client, elementToDisplay) => {
    const isClientSynchronizable = client.syncType == ELIGIBILITY.value;
    let displayForManual = false;
    if (client.clientBillingPeriodType == MANUAL.value && elementToDisplay) {
        if (
            typeof elementToDisplay === "object" &&
            !Array.isArray(elementToDisplay) &&
            Object.hasOwn(elementToDisplay, "value")
        ) {
            elementToDisplay = elementToDisplay.value;
        }
        const elementsToDisplay = [MEMBER.value, DEPENDENT_PERSON.value];
        displayForManual = elementsToDisplay.includes(elementToDisplay) && [ELIGIBILITY.value, NO_SYNC.value].includes(client.syncType);
    }
    return userHasAccess(INTERNAL_ROLES, true) && (isClientSynchronizable || displayForManual);
};

export const canViewSyncForTEDDesjardins = (client) => {
    return userHasAccess(INTERNAL_ROLES, true) && client && client.syncType === TED_DESJARDINS.value;
};



// WEBPACK FOOTER //
// ./src/components/common/synchronization/SynchronizationHelper.js